import { ApolloError, useApolloClient, useMutation } from '@apollo/client';
import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { isMediaQuestion } from '../components/questions/Question';
import {
  AnswerInput,
  Decode,
  Decode_decode,
  Decode_decode_styles,
  DecodeInput,
  preloadedAnswersInput,
  Question,
  QuestionGroup,
  QuestionInput,
  QuestionType,
  Structure,
  Structure_structure,
  Structure_structure_questionGroups,
  Submission,
  SubmitEditResult,
  SubmitResult,
  SubmitResultData,
} from '../customGraphqlTypes';
import { DecodeGQL, StructureGQL, SubmitEditGQL, SubmitGQL } from '../queries/SubmissionQueries';
import { getSubmissionByExternalId } from '../services/SubmissionService';
import { usePicturesContext } from './PicturesContext';

export interface StorageManager {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
}

type SubmissionContextType = {
  decode?: InternalDecode;
  structure?: InternalStructure;
  questionGroups?: InternalQuestionGroups[];
  questionGroupBeingEdited?: InternalQuestionGroups;
  lastActiveQuestionGroup?: InternalQuestionGroups;
  loading: boolean;
  isEditingSubmission: boolean;
  isReadyForSubmit: boolean;
  goBackToQuestions: () => void;
  skipVIN: boolean;
  submittingStructure: boolean;
  submissionId?: string;
  submissionError?: Error;
  preloadedPictures?: any[];
  submitVIN: (vin: string) => void;
  getSubmissionData: () => Submission;
  submitStructure: () => void;
  selectStyle: (style: Decode_decode_styles) => void;
  answerQuestionGroup: (questionGroup: InternalQuestionGroups) => void;
  activateQuestionGroup: (questionGroup: InternalQuestionGroups, editing: boolean) => void;
  activateLast: () => void;
  discardQuestionGroup: (questionGroup: InternalQuestionGroups) => void;
  reEvaluateQuestionGroupsDisplayRules: () => void;
  clearSubmission: () => void;
  isEditingQuestionGroup: boolean;
  setIsEditingQuestionGroup: (value: boolean) => void;
  deactivateQuestionGroups: () => void;
  /**
   * When set to true, allows to show a prompt to the user
   * to check whether to continue answering questions or not
   */
  requireContinueConfirmation: (require: boolean) => void;
  askContinueConfirmation: boolean;
  cancelSubmission: boolean;
  setCancelSubmission: (cancel: boolean) => void;
  preloadedData: PreloadedData;
  checkIfVinExists?: (vin: string) => any;
  questionBeingAnswered?: InternalQuestion;
  answeredNumberOfQuestionGroups: number;
  totalNumberOfQuestionGroups: number;
  allQuestionGroupsAnswered: boolean;
  allQuestionAnswered: (questionGroup: InternalQuestionGroups) => boolean;
};

export const SubmissionContext = React.createContext(undefined as SubmissionContextType);

export interface InternalQuestion extends Omit<Question, 'answer' | 'type'> {
  tempAnswer?: any;
  active: boolean;
  visible: boolean;
  type: any;
  answer?: AnswerInput[];
  customQuestionUI?: React.ReactNode;
  noInteractiveTitle?: boolean;
  onAnswerCallback?: (question: InternalQuestion) => void;
}

export interface InternalQuestionGroups extends Omit<Structure_structure_questionGroups, 'questions'> {
  active: boolean;
  answered: boolean;
  visible: boolean;
  questions: (InternalQuestion | null)[] | null;
  noActionsButtons?: boolean;
  answerOnlyOnce?: boolean;
  excludeFromSubmission?: boolean;
  accessoryModal?: {
    cta: string;
    modal: {
      title: string;
      body: string[];
    };
  };
}

export interface InternalStructure extends Omit<Structure_structure, 'questionGroups'> {
  questionGroups: (InternalQuestionGroups | null)[] | null;
}

export interface InternalDecode extends Decode_decode {
  style?: Decode_decode_styles;
  errors?: DecodeError[];
  vehiclePhoto?: string;
}

interface Error {
  code: string;
  message: string;
}

type DecodeError = Error;

export type SubmissionOnSubmit = (submission: Submission) => any;
export type SubmissionOnCancel = (submission: Submission | null) => any;
export type SubmissionOnError = (errorMessage: string) => any;

export const PUBLIC_ERROR_CODE = 'PUBLIC_ERROR_CODE';

interface SubmissionProviderProps {
  externalId?: string;
  editMode?: boolean;
  onSubmit: SubmissionOnSubmit;
  children: ReactNode;
  storageManager: StorageManager;
  structureType?: string;
  submission?: Submission;
  skipVIN?: boolean;
  preloadedData?: PreloadedData;
  enableDrafts?: boolean;
  vinExistsValidation?: (vin: string) => Promise<any>;
  questionGroupsCallback?: (qg: InternalQuestionGroups[]) => InternalQuestionGroups[];
  storageId?: string;
}

export const storageKey = 'SubmissionProviderCurrentSubmissionData';

interface VCPAnswersData {
  [key: string]: any;
}

export interface PreloadedData {
  vin: string;
  make?: string;
  model?: string;
  year?: string;
  provider?: string;
  styleId?: string;
  title?: string;
  series?: string;
  decode?: Decode_decode;
  answers?: VCPAnswersData;
}

export const SubmissionProvider = ({
  children,
  editMode,
  externalId,
  onSubmit,
  storageManager,
  structureType,
  submission,
  skipVIN,
  preloadedData,
  enableDrafts,
  vinExistsValidation,
  questionGroupsCallback,
  storageId,
}: SubmissionProviderProps) => {
  externalId = externalId && `${externalId}`;
  const [decode, setDecode] = useState<InternalDecode | undefined>();
  const [questionGroups, setQuestionGroups] = useState<InternalQuestionGroups[]>();
  const [lastActiveQuestionGroup, setLastActiveQuestionGroup] = useState<InternalQuestionGroups>();
  const [isDecoding, setIsDecoding] = useState(false);
  const [isReadyForSubmit, setIsReadyForSubmit] = useState(false);
  const [submissionError, setSubmissionError] = useState<Error | undefined>();
  const [submissionId, setSubmissionId] = useState<string | undefined>();
  const [preloadedPictures, setPreloadedPictures] = useState<any>();
  const [checkedForDraft, setCheckedForDraft] = useState<boolean>(false);
  const [questionGroupBeingEdited, setQuestionGroupBeingEdited] = useState<InternalQuestionGroups | undefined>();
  const [isEditingQuestionGroup, setIsEditingQuestionGroup] = useState<boolean>(false);
  const [askContinueConfirmation, setAskContinueConfirmation] = useState(false);
  const [cancelSubmission, setCancelSubmission] = useState(false);
  const [structure, setStructure] = useState<Structure_structure | null>();
  const [answeredNumberOfQuestionGroups, setAnsweredNumberOfQuestionGroups] = useState<number>(0);
  const [totalNumberOfQuestionGroups, setTotalNumberOfQuestionGroups] = useState<number>(0);
  const [preloadedInitialData, setPreloadedInitialData] = useState<PreloadedData | undefined>(preloadedData);
  const [isCheckingIfVinExists, setIsCheckingIfVinExists] = useState<boolean>(false);

  const storageIdentifer = `${storageKey}_${storageId ?? 'v1'}`;

  const {
    setPicturesFromRetrievedSubmission,
    updateLastSubmittedPictures,
    getLastQuestionPictures,
    restoreSavedPictures,
  } = usePicturesContext();

  const client = useApolloClient();

  const isEditingSubmission = !!editMode || !!submission;

  const allQuestionGroupsAnswered = useMemo(
    () => answeredNumberOfQuestionGroups === totalNumberOfQuestionGroups && totalNumberOfQuestionGroups > 0,
    [answeredNumberOfQuestionGroups, totalNumberOfQuestionGroups],
  );

  const requireContinueConfirmation = (require: boolean) => {
    setAskContinueConfirmation(require);
  };

  const normalizeQuestions = useCallback((questions: Question[]): InternalQuestion[] => {
    return (
      questions?.reduce((filtered, question) => {
        if (!question.options || question.options?.length || question.type === QuestionType.units) {
          filtered.push({
            ...question,
            active: false,
            visible: false,
          });
        }
        return filtered;
      }, []) || []
    );
  }, []);

  const onDecodeOrLoadStructureError = useCallback((error) => {
    const { graphQLErrors, networkError, message } = error;

    const errors: DecodeError[] =
      graphQLErrors?.map((error) => ({
        code: error.extensions?.code,
        message: error.message,
      })) || [];

    if (networkError) {
      errors.push({
        code: 'NETWORK_ERROR',
        message: networkError?.message,
      });
    }

    if (!errors.length && (message || error)) {
      // in case this was any other kind of error that didn't have graphQLErrors or networkError
      // properties, push the message or error object so we don't end up with an empty errors
      // array
      errors.push({
        code: 'GENERAL',
        message: (message as string) || (error as string),
      });
    }

    setDecode({
      vin: null,
      vehiclePhoto: null,
      make: null,
      model: null,
      style: null,
      styles: null,
      year: null,
      errors: [...errors],
    });
  }, []);

  const buildSubmissionError = (e: ApolloError): Error => {
    const { graphQLErrors } = e;
    const error = graphQLErrors?.[0];
    const errorCode = error?.extensions?.code as string;
    const errorMessage = error?.message;

    return {
      code: errorCode,
      message: errorMessage ? errorMessage : e.message,
    };
  };

  const handleSubmitOnCompleted = (data: SubmitResultData) => {
    const submissionId = data?.id;
    const externalSubmissionId = data?.externalId;

    setSubmissionId(submissionId);

    const submission: Submission = {
      id: submissionId,
      externalId: externalSubmissionId,
      structure: getSubmissionData().structure,
    };

    clearSubmission();
    onSubmit?.(submission);
  };

  const [submitStructureMutation, { loading: submittingNewStructure }] = useMutation<SubmitResult>(SubmitGQL, {
    onCompleted(data) {
      handleSubmitOnCompleted(data?.submit);
    },
    onError(e) {
      const error = buildSubmissionError(e);
      setSubmissionError(error);
    },
  });

  const [submitEditStructureMutation, { loading: submittingEditStructure }] = useMutation<SubmitEditResult>(
    SubmitEditGQL,
    {
      onCompleted(data) {
        handleSubmitOnCompleted(data?.editSubmission);
      },
      onError(e) {
        const error = buildSubmissionError(e);
        setSubmissionError(error);
      },
    },
  );

  const submittingStructure = submittingNewStructure || submittingEditStructure;
  const [isLoadingStructure, setIsLoadingStructure] = useState<boolean>(false);
  const loading = isLoadingStructure || isDecoding || isCheckingIfVinExists;

  const submitVIN = useCallback(
    (vin: string) => {
      setIsDecoding(true);

      void (async () => {
        try {
          const result = await client.query<Decode>({
            query: DecodeGQL,
            variables: { vin },
          });

          setIsDecoding(false);

          const data = result?.data;

          setDecode({
            ...data?.decode,
            style: data?.decode?.styles?.length === 1 ? data?.decode?.styles?.[0] : undefined,
            errors: [],
          });
        } catch (error) {
          setIsDecoding(false);
          onDecodeOrLoadStructureError(error);
        }
      })();
    },
    [client, onDecodeOrLoadStructureError],
  );

  const deactivateQuestionGroups = () => {
    setQuestionGroupBeingEdited(undefined);
    if (questionGroups) {
      questionGroups?.forEach((q) => (q.active = false));
      setQuestionGroups([...questionGroups]);
    }
  };

  const updateAnswersForQuestionsWithPictures = () => {
    questionGroups?.forEach((questionGroup) => {
      questionGroup.questions?.forEach((question) => {
        if ([QuestionType.gallery, QuestionType.media, QuestionType.medianote].includes(question.type)) {
          answerQuestion(question);
        }
      });
    });
  };

  const getSubmissionData = (): Submission | null => {
    if ((!decode && !skipVIN) || !questionGroups) {
      return null;
    }
    const decodeInput: DecodeInput = {
      vin: decode?.vin,
      year: decode?.year,
      make: decode?.make,
      model: decode?.model,
      provider: decode?.style?.provider,
      styleId: decode?.style?.styleId,
      styleName: decode?.style?.title,
      valuations: structure?.decode?.valuations,
      valuationsByMileage: structure?.decode?.valuationsByMileage,
    };

    const submittableQuestionsGroups = questionGroups.filter((qg) => !qg.excludeFromSubmission);

    // Sanitize question groups removing extra properties
    const questionGroupsInput: QuestionGroup[] = submittableQuestionsGroups.map((questionGroup) => {
      const { title, key, displayRule, editable, questions: plainQuestions, category } = questionGroup;
      const questions: QuestionInput[] = plainQuestions.map((plainQuestion) => {
        const { active, visible, tempAnswer, answer, ...question } = plainQuestion;
        return { ...question, answer };
      });

      return { title, key, displayRule, editable, questions, category };
    });
    return {
      structure: {
        type: structureType,
        decode: decodeInput,
        questionGroups: questionGroupsInput,
      },
    };
  };

  const submitStructure = () => {
    deactivateQuestionGroups();

    updateAnswersForQuestionsWithPictures();

    const submissionData: Submission = getSubmissionData();

    setSubmissionError(undefined);
    if (isEditingSubmission || externalId) {
      void submitEditStructureMutation({
        variables: {
          structure: submissionData.structure,
          externalId: externalId || submission?.externalId,
        },
      });
    } else {
      void submitStructureMutation({
        variables: {
          structure: submissionData.structure,
        },
      });
    }
  };

  const selectStyle = (style: Decode_decode_styles) => {
    if (!style) {
      return;
    }

    setDecode({ ...decode, style: style });
  };

  const activateQuestionGroup = (questionGroup: InternalQuestionGroups, editing = false) => {
    questionGroups?.forEach((q) => (q.active = false));
    questionGroup.active = true;
    questionGroup.visible = true;
    if (questionGroups) {
      setQuestionGroups([...questionGroups]);
    }
    setLastActiveQuestionGroup(questionGroup);
    setIsReadyForSubmit(false);
    setQuestionGroupBeingEdited(editing ? questionGroup : undefined);
  };

  const evalDisplayRule = (
    questionGroup: InternalQuestionGroups,
    contextQuestionGroups: InternalQuestionGroups[] = questionGroups,
  ) => {
    if (!questionGroup.displayRule) {
      return true;
    }

    try {
      // eslint-disable-next-line @typescript-eslint/no-implied-eval
      const deserializedDisplayRule = new Function('return ' + questionGroup.displayRule)();
      const structure = { questionGroups: contextQuestionGroups };
      return deserializedDisplayRule(structure);
    } catch {
      // In case of error show the question group anyway
      return true;
    }
  };

  const reEvaluateQuestionGroupsDisplayRules = () => {
    questionGroups?.forEach((questionGroup) => {
      questionGroup.visible = evalDisplayRule(questionGroup);
    });
    if (questionGroups) {
      setQuestionGroups([...questionGroups]);
    }
  };

  const activateLast = () => {
    if (questionGroups) {
      const item = questionGroups?.reduce((firstNonAnsweredQuestionGroup, questionGroup) => {
        const shouldDisplay = evalDisplayRule(questionGroup);

        if (shouldDisplay) {
          if (firstNonAnsweredQuestionGroup) {
            if (!isEditingSubmission) {
              questionGroup.answered = false;
              questionGroup.visible = false;
            }
          } else {
            if (!questionGroup.answered) {
              firstNonAnsweredQuestionGroup = questionGroup;
            }
          }
        }
        return firstNonAnsweredQuestionGroup;
      }, null);

      if (item) {
        const questionGroupIndex = questionGroups?.findIndex((qg) => qg.key === item.key);
        setAnsweredNumberOfQuestionGroups(questionGroupIndex + 1);
        activateQuestionGroup(item);
        setIsReadyForSubmit(false);
      } else {
        setIsReadyForSubmit(true);
        deactivateQuestionGroups();
      }
    }
  };

  const persistStructure = () => {
    if (enableDrafts) {
      const data = JSON.stringify({ decode, questionGroups, preloadedData });
      void storageManager.setItem(storageIdentifer, data);
    }
  };

  const answerQuestion = (question: InternalQuestion) => {
    if (question?.tempAnswer === undefined) {
      return;
    }

    question.onAnswerCallback?.(question);

    if (isMediaQuestion(question)) {
      updateLastSubmittedPictures();
    }

    switch (question.type) {
      case QuestionType.singleselect:
      case QuestionType.multiselect:
      case QuestionType.singlemultiselect:
        question.answer = [...question.tempAnswer];
        break;
      case QuestionType.gallery:
      case QuestionType.media:
        question.answer = question.tempAnswer
          ?.filter((picture) => !picture.failed && picture.url)
          .map((picture) => ({ value: picture.url, extra: [picture.placeholderName] }));
        break;
      case QuestionType.medianote:
        question.answer = [
          {
            value: question.tempAnswer?.value,
            extra: question.tempAnswer?.pictures.filter((picture) => !picture.failed).map((picture) => picture.url),
          },
        ];
        break;
      case QuestionType.units:
      case QuestionType.currency:
        question.answer = [
          {
            value: question.tempAnswer?.value,
            extra: [question.tempAnswer?.extra || ''],
          },
        ];
        break;
      case QuestionType.note:
        question.answer = [{ value: question.tempAnswer?.value }];
        break;
      case QuestionType.date:
        question.answer = [{ value: question.tempAnswer?.value }];
        break;
      case 'address' as QuestionType:
        question.answer = [{ ...question.tempAnswer }];
        break;
    }
  };

  const answerQuestionGroup = (questionGroup: InternalQuestionGroups) => {
    if (questionGroup) {
      questionGroup.questions?.forEach((question) => answerQuestion(question));

      questionGroup.answered = true;

      if (!isEditingSubmission) {
        persistStructure();
      }
      if (questionGroups) {
        setQuestionGroups([...questionGroups]);
      }
    }
  };

  const discardQuestionGroup = (questionGroup: InternalQuestionGroups) => {
    questionGroup.questions?.forEach((question) => {
      if (!question || !question?.answer) {
        return;
      }

      if (isMediaQuestion(question)) {
        restoreSavedPictures();
      }
      // Restore the question temp answer based on the previous answer
      switch (question.type) {
        case QuestionType.singleselect:
        case QuestionType.multiselect:
        case QuestionType.singlemultiselect:
          question.tempAnswer = [...question.answer];
          break;
        case QuestionType.gallery:
        case QuestionType.media: {
          const lastPictures = getLastQuestionPictures(question.key);
          question.tempAnswer = lastPictures;
          break;
        }
        case QuestionType.medianote: {
          const lastPictures = getLastQuestionPictures(question.key);

          question.tempAnswer.pictures = lastPictures;
          break;
        }
        case QuestionType.units:
        case QuestionType.currency: {
          const answer = question.answer?.[0];
          question.tempAnswer = { value: answer?.value, extra: answer?.extra?.[0] };
          break;
        }
        case QuestionType.note:
          question.tempAnswer = { value: question.answer?.[0]?.value };
          break;
        case QuestionType.date:
          question.tempAnswer = { value: question.answer?.[0]?.value };
          break;
        case 'address' as QuestionType:
          question.tempAnswer = { ...question.answer[0] };
          break;
      }
    });
    if (questionGroups) {
      setQuestionGroups([...questionGroups]);
    }
  };

  const getAnswersFromPreloadedData = (): preloadedAnswersInput[] => {
    if (!preloadedData?.answers) {
      return;
    }
    const answersData: preloadedAnswersInput[] = [];
    const preloadAnswers = preloadedData?.answers;
    Object.keys(preloadAnswers).forEach(function (key) {
      if (key.includes('Unit')) {
        // update the extra field with unit of the original attribue
        const attOriginalKey = key.replace('Unit', '');
        const dataItem = answersData.find((item) => item.questionKey === attOriginalKey);
        if (dataItem?.answer?.[0]) {
          dataItem.answer[0].extra = [preloadAnswers[key]];
        }
      } else {
        // create the new question answer by key
        const objectValue = preloadAnswers[key];
        const newItem: preloadedAnswersInput = {
          questionKey: key,
        };

        // Check if the value is an array
        if (Array.isArray(objectValue)) {
          newItem.answer = objectValue.map((value) => ({
            code: value,
            value: value ?? '',
          }));
        } else {
          newItem.answer = [
            {
              code: objectValue,
              value: objectValue ?? '',
            },
          ];
        }
        answersData.push(newItem);
      }
    });
    return answersData;
  };

  const allQuestionAnswered = (questionGroup: any) => {
    const hasUnansweredQuestion = questionGroup.questions.some(
      (question: InternalQuestion) => question.answer === null,
    );
    return !hasUnansweredQuestion;
  };

  const loadStructure = useCallback(async () => {
    try {
      setIsLoadingStructure(true);
      const preloadedAnswers = getAnswersFromPreloadedData();

      // using client.query since useLazyQuery wasn't calling the onError/onCompleted callbacks if trying to send
      // the same query multiple times
      const result = await client.query<Structure>({
        query: StructureGQL,
        variables: {
          vin: decode?.vin || '',
          type: structureType,
          style: {
            provider: decode?.style?.provider || '',
            styleId: decode?.style?.styleId || '',
          },
          preloadedAnswers: preloadedAnswers,
        },
      });

      const data = result?.data;

      const questionGroups = data?.structure?.questionGroups.reduce((filtered, questionGroup) => {
        const questions = normalizeQuestions(questionGroup?.questions);
        if (questions?.length) {
          filtered.push({
            ...questionGroup,
            answered: allQuestionAnswered(questionGroup) ? true : false,
            active: false,
            questions,
          });
        } else {
          /**
           * Question groups with a title and without questions (empty array)
           * are interpreted as informational texts
           */
          filtered.push({
            ...questionGroup,
            answered: false,
            active: false,
            questions: [],
          });
        }
        return filtered;
      }, []);

      const isSelectionType = (type: string): boolean => {
        return (
          type === QuestionType.singleselect ||
          type === QuestionType.multiselect ||
          type === QuestionType.singlemultiselect
        );
      };

      if (preloadedAnswers) {
        questionGroups.forEach((qg) =>
          qg.questions.forEach((q) => {
            if (q.answer) {
              qg.visible = evalDisplayRule(qg);
              const answer = q.answer;
              if (isSelectionType(q.type)) {
                q.tempAnswer = answer;
              } else {
                if (q.type === QuestionType.units) {
                  const auxAnswer = answer.map((a) => {
                    const auxAnswer = { ...a };
                    auxAnswer['extra'] = a?.extra?.[0];
                    return auxAnswer;
                  });
                  q.tempAnswer = auxAnswer?.[0];
                } else {
                  q.tempAnswer = answer?.[0];
                }
              }
            }
          }),
        );
      }

      questionGroups[0].active = true;
      questionGroups[0].visible = true;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      const getFirstNotAnsweredQuestionGroup = () => {
        return questionGroups.find((questionGroup: InternalQuestionGroups) => questionGroup.answered === false);
      };
      setLastActiveQuestionGroup(getFirstNotAnsweredQuestionGroup());
      const finalQuestionGroups = questionGroupsCallback ? questionGroupsCallback(questionGroups) : questionGroups;
      setQuestionGroups(finalQuestionGroups);

      setDecode({
        ...decode,
        ...data?.structure?.decode,
      });

      setStructure(data?.structure);
      setTotalNumberOfQuestionGroups(finalQuestionGroups?.length);
    } catch (error) {
      onDecodeOrLoadStructureError(error);
    } finally {
      setIsLoadingStructure(false);
    }
  }, [decode?.vin, decode?.style, normalizeQuestions, onDecodeOrLoadStructureError, structureType]);

  const getPictures = (questionGroups) => {
    const pictures: any = [];

    const addPicture = (url, questionKey, placeholderName?: string) => {
      pictures.push({
        url: url,
        thumbnail: url,
        uploading: false,
        failed: false,
        questionId: questionKey,
        placeholderName,
      });
    };

    questionGroups?.forEach((questionGroup) => {
      questionGroup?.questions?.forEach((question) => {
        if (!question || !question?.answer) {
          return;
        }
        // Restore the question temp answer based on the previous answer
        switch (question.type) {
          case QuestionType.gallery:
          case QuestionType.media:
            question.answer?.forEach((answer) => {
              if (!answer?.value) {
                return;
              }
              addPicture(answer.value, question.key, answer.extra?.[0] as string);
            });
            break;
          case QuestionType.medianote:
            question.answer?.[0]?.extra?.forEach((answer) => {
              if (!answer) {
                return;
              }
              addPicture(answer, question.key);
            });
            break;
        }
      });
    });

    return pictures;
  };

  const getTempAnswer = (question: any) => {
    if (!question || !question?.answer) {
      return;
    }
    // Restore the question temp answer based on the previous answer
    switch (question.type) {
      case QuestionType.singleselect:
      case QuestionType.multiselect:
      case QuestionType.singlemultiselect:
        return [...question.answer];
      case QuestionType.gallery:
      case QuestionType.media:
        return question.answer?.reduce((pictures, answer) => {
          if (!answer?.value) {
            return;
          }
          pictures.push({
            url: answer.value,
            thumbnail: answer.value,
            uploading: false,
            failed: false,
            questionId: question.key,
            placeholderName: answer.extra?.[0] as string,
          });
          return pictures;
        }, []);
      case QuestionType.medianote: {
        const pictures = question.answer?.[0]?.extra?.reduce((resultPictures, answer) => {
          if (!answer) {
            return;
          }
          resultPictures.push({
            url: answer,
            thumbnail: answer,
            uploading: false,
            failed: false,
            questionId: question.key,
          });
          return resultPictures;
        }, []);
        return { value: question.answer?.[0]?.value, pictures };
      }
      case QuestionType.units:
      case QuestionType.currency: {
        const answer = question.answer?.[0];
        return { value: answer?.value, extra: answer?.extra?.[0] };
      }
      case QuestionType.note:
      case QuestionType.date:
        return { value: question.answer?.[0]?.value };
      case 'address' as QuestionType:
        return { ...question.answer[0] };
    }
  };

  const questionGroupBeingAnswered = useMemo(() => questionGroups?.find((qg) => qg.active), [questionGroups]);

  const questionBeingAnswered = useMemo(() => {
    if (isEditingQuestionGroup) {
      return questionGroupBeingEdited?.questions[0];
    }
    if (!questionGroupBeingAnswered) {
      return;
    }
    return questionGroupBeingAnswered?.questions?.find((q) => {
      return !q.answer && !q.tempAnswer;
    });
  }, [questionGroupBeingAnswered, isEditingQuestionGroup, questionGroupBeingEdited]);

  const loadSubmissionToEdit = async (externalId: string) => {
    setIsLoadingStructure(true);
    const submission = await getSubmissionByExternalId(externalId, client, structureType);

    loadSubmission(submission);
  };

  const loadSubmission = (submission: Submission) => {
    try {
      setIsLoadingStructure(true);

      const data = submission.structure;

      setDecode({
        ...data?.decode,
        style: {
          provider: data?.decode?.provider,
          styleId: data?.decode?.styleId,
          title: data?.decode?.styleName,
          series: '',
        },
        styles: [],
      } as InternalDecode);

      const questionGroups = data?.questionGroups.reduce((filteredQuestionGroups, questionGroup) => {
        const questions = normalizeQuestions(questionGroup?.questions as Question[]);
        questions.forEach((question) => {
          question.tempAnswer = getTempAnswer(question);
        });
        if (questions.length) {
          filteredQuestionGroups.push({
            ...(questionGroup as InternalQuestionGroups),
            visible: false,
            answered: true,
            active: false,
            questions,
          });
        }
        return filteredQuestionGroups;
      }, []);

      const picturesToBeSet = getPictures(questionGroups);
      setPreloadedPictures(picturesToBeSet);
      setPicturesFromRetrievedSubmission(picturesToBeSet);

      // Evaluate display rules
      questionGroups?.forEach((questionGroup) => {
        questionGroup.visible = evalDisplayRule(questionGroup, questionGroups);
        if (isEditingSubmission) {
          questionGroup.answered = evalDisplayRule(questionGroup, questionGroups);
        }
      });

      setQuestionGroups(questionGroups);
      setLastActiveQuestionGroup(questionGroups?.[questionGroups.length - 1]);
      setStructure(submission.structure as any);
      setTotalNumberOfQuestionGroups(questionGroups?.length);
      setAnsweredNumberOfQuestionGroups(questionGroups?.length);
    } catch (error) {
      onDecodeOrLoadStructureError(error);
    } finally {
      setIsLoadingStructure(false);
    }
  };

  const clearSubmission = () => {
    void storageManager.removeItem(storageIdentifer);
  };

  const checkIfVinExists = async (vin: string) => {
    if (!vinExistsValidation) return undefined;
    setIsCheckingIfVinExists(true);
    const callback = await vinExistsValidation(vin);
    setIsCheckingIfVinExists(false);
    return callback;
  };

  const goBackToQuestions = () => {
    setIsReadyForSubmit(false);
    setIsEditingQuestionGroup(true);
    // Activate last answered question
    const questionGroupsLocal = questionGroups.slice();
    questionGroupsLocal[questionGroupsLocal.length - 1].active = true;
    questionGroupsLocal[questionGroupsLocal.length - 1].visible = true;
    setLastActiveQuestionGroup(questionGroupsLocal?.[questionGroupsLocal.length - 1]);
    setQuestionGroups(questionGroupsLocal);
  };

  useEffect(() => {
    if (
      !decode?.vin ||
      !decode?.style ||
      isEditingSubmission ||
      !checkedForDraft ||
      questionGroups?.length > 0 ||
      submission
    ) {
      return;
    }

    void loadStructure();
  }, [decode?.style, loadStructure, isEditingSubmission, checkedForDraft, submission]);

  useEffect(() => {
    if (!externalId || !isEditingSubmission) {
      return;
    }

    void loadSubmissionToEdit(externalId);
  }, [externalId, isEditingSubmission]);

  useEffect(() => {
    if (!checkedForDraft || !!decode || !preloadedData?.vin) return;
    if (!preloadedData.decode) {
      submitVIN(preloadedData.vin);
    } else {
      setDecode({ ...preloadedData.decode, style: preloadedData?.decode?.styles?.[0] });
    }
  }, [preloadedData, checkedForDraft]);

  useEffect(() => {
    if (isEditingSubmission) {
      return;
    }

    const fetchDraft = async () => {
      if (enableDrafts) {
        const draftData = await storageManager.getItem(storageIdentifer);
        if (draftData) {
          const { decode, questionGroups, preloadedData } = JSON.parse(draftData ?? '') as {
            decode: InternalDecode;
            questionGroups: InternalQuestionGroups[];
            preloadedData: PreloadedData;
          };

          if (preloadedData && preloadedData.vin !== decode.vin) {
            return;
          }

          if (preloadedData) {
            setPreloadedInitialData(preloadedData);
          }
          if (decode) setDecode(decode);
          if (questionGroups) {
            const finalQuestionGroups = questionGroupsCallback
              ? questionGroupsCallback(questionGroups)
              : questionGroups;
            setQuestionGroups(finalQuestionGroups);
            setPreloadedPictures(getPictures(questionGroups));
          }
          setStructure({
            decode,
            questionGroups,
          } as any);
          setTotalNumberOfQuestionGroups(questionGroups?.length);
          setAnsweredNumberOfQuestionGroups(questionGroups?.length);
        }
      }
    };

    fetchDraft()
      .catch()
      .finally(() => setCheckedForDraft(true));
  }, [storageManager, preloadedData, enableDrafts]);

  useEffect(() => {
    if (!externalId && submission) {
      loadSubmission(submission);
    }
  }, [submission, externalId]);

  return (
    <SubmissionContext.Provider
      value={{
        activateLast,
        activateQuestionGroup,
        allQuestionAnswered,
        answerQuestionGroup,
        decode,
        discardQuestionGroup,
        preloadedPictures,
        isEditingSubmission,
        isReadyForSubmit,
        goBackToQuestions,
        loading,
        questionGroups,
        questionGroupBeingEdited,
        lastActiveQuestionGroup,
        reEvaluateQuestionGroupsDisplayRules,
        selectStyle,
        submissionError,
        submissionId,
        getSubmissionData,
        skipVIN,
        submitStructure,
        submitVIN,
        submittingStructure,
        clearSubmission,
        isEditingQuestionGroup,
        setIsEditingQuestionGroup,
        deactivateQuestionGroups,
        requireContinueConfirmation,
        askContinueConfirmation,
        cancelSubmission,
        setCancelSubmission,
        preloadedData: preloadedInitialData,
        checkIfVinExists,
        questionBeingAnswered,
        answeredNumberOfQuestionGroups,
        totalNumberOfQuestionGroups,
        allQuestionGroupsAnswered,
      }}
    >
      {children}
    </SubmissionContext.Provider>
  );
};

export function useSubmissionContext() {
  return React.useContext(SubmissionContext);
}
